import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './ShareTableModal.css';

const ShareTableModal = ({ table, onClose }) => {
  const { token } = useAuth();
  const [username, setUsername] = useState('');
  const [permission, setPermission] = useState('read');
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    fetchShares();
  }, [table.id]);

  const fetchShares = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shares/table/${table.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setShares(data);
      }
    } catch (err) {
      console.error('Error fetching shares:', err);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/shares`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          table_id: table.id,
          shared_with_username: username,
          permission: permission
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to share table';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, include status code
          if (response.status === 401) {
            errorMessage = 'Session expired. Please log in again.';
          } else if (response.status === 404) {
            errorMessage = 'User not found or table not found';
          } else {
            errorMessage = `Failed to share table (Status: ${response.status})`;
          }
        }
        throw new Error(errorMessage);
      }

      // Refresh shares list
      await fetchShares();
      const sharedUsername = username;
      setUsername('');
      setPermission('read');
      setSuccessMessage(`Successfully shared with ${sharedUsername}`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      // Check if it's a network error
      if (err.message === 'Failed to fetch') {
        setError('Network error. Please check your connection and ensure the backend is running.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    if (!window.confirm('Remove this share?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/shares/${shareId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchShares();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdatePermission = async (shareId, newPermission) => {
    try {
      const response = await fetch(`${API_URL}/api/shares/${shareId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          permission: newPermission
        })
      });

      if (response.ok) {
        await fetchShares();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>Share Table: {table.name}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="share-modal-content">
          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message" style={{
              padding: '12px',
              background: '#10b981',
              color: 'white',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {successMessage}
            </div>
          )}

          <form onSubmit={handleShare} className="share-form">
            <h3>Share with User</h3>
            <div className="form-group">
              <label>Username:</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
              />
            </div>

            <div className="form-group">
              <label>Permission:</label>
              <select value={permission} onChange={(e) => setPermission(e.target.value)}>
                <option value="read">Read Only</option>
                <option value="write">Read & Write</option>
              </select>
            </div>

            <button type="submit" disabled={loading} className="share-button">
              {loading ? 'Sharing...' : 'Share Table'}
            </button>
          </form>

          <div className="existing-shares">
            <h3>Shared With</h3>
            {shares.length === 0 ? (
              <p className="no-shares">Not shared with anyone yet</p>
            ) : (
              <div className="shares-list">
                {shares.map((share) => (
                  <div key={share.id} className="share-item">
                    <div className="share-info">
                      <span className="share-username">{share.shared_with_username}</span>
                      <select
                        value={share.permission}
                        onChange={(e) => handleUpdatePermission(share.id, e.target.value)}
                        className="permission-select"
                      >
                        <option value="read">Read Only</option>
                        <option value="write">Read & Write</option>
                      </select>
                    </div>
                    <button
                      onClick={() => handleDeleteShare(share.id)}
                      className="delete-share-button"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareTableModal;
